/**
 * Auto-detect user's timezone using browser's Intl API
 * This provides a reliable way to get the user's timezone without external libraries
 */

/**
 * Detects user's timezone using JavaScript's Intl API
 * Returns the IANA timezone string (e.g., 'America/Chicago', 'Europe/London')
 * Falls back to 'UTC' if detection fails
 */
export const detectUserTimezone = (): string => {
  try {
    // Use Intl API to get the timezone from the user's browser settings
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone && typeof timeZone === 'string' && timeZone.trim()) {
      return timeZone;
    }
  } catch (error) {
    console.warn('Failed to detect timezone using Intl API:', error);
  }

  // Fallback to UTC if detection fails
  return 'UTC';
};

/**
 * Gets the user's timezone preference
 * 1. First checks localStorage for saved timezone
 * 2. If not saved, auto-detects using Intl API
 * 3. Saves the auto-detected timezone to localStorage
 */
export const getOrDetectUserTimezone = (): string => {
  try {
    // Check if already saved in localStorage
    const saved = localStorage.getItem('user_timezone');
    if (saved && saved.trim()) {
      return saved;
    }

    // Auto-detect if not saved
    const detected = detectUserTimezone();
    localStorage.setItem('user_timezone', detected);
    return detected;
  } catch (error) {
    console.error('Failed to get or detect timezone:', error);
    return 'UTC';
  }
};

/**
 * Clears the saved timezone from localStorage
 * Useful when user updates their timezone preference
 */
export const clearSavedTimezone = (): void => {
  try {
    localStorage.removeItem('user_timezone');
  } catch (error) {
    console.warn('Failed to clear saved timezone:', error);
  }
};

/**
 * List of common IANA timezones for reference
 * Used for timezone selection dropdowns
 */
export const COMMON_TIMEZONES = [
  // Americas
  { label: 'Eastern Time (US & Canada)', value: 'America/New_York' },
  { label: 'Central Time (US & Canada)', value: 'America/Chicago' },
  { label: 'Mountain Time (US & Canada)', value: 'America/Denver' },
  { label: 'Pacific Time (US & Canada)', value: 'America/Los_Angeles' },
  { label: 'Alaska Time', value: 'America/Anchorage' },
  { label: 'Hawaii Time', value: 'Pacific/Honolulu' },

  // Europe
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'Central European Time', value: 'Europe/Berlin' },
  { label: 'Paris Time', value: 'Europe/Paris' },

  // Asia
  { label: 'India Standard Time', value: 'Asia/Kolkata' },
  { label: 'Singapore Time', value: 'Asia/Singapore' },
  { label: 'Hong Kong Time', value: 'Asia/Hong_Kong' },
  { label: 'Tokyo Time', value: 'Asia/Tokyo' },
  { label: 'Sydney Time', value: 'Australia/Sydney' },

  // Other
  { label: 'UTC (Coordinated Universal Time)', value: 'UTC' },
];

const TIMEZONE_ALIASES: Record<string, string> = {
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Pacific': 'America/Los_Angeles',
  'US/Mountain': 'America/Denver',
  'US/Central': 'America/Chicago',
  'US/Eastern': 'America/New_York',
  'US/Samoa': 'Pacific/Pago_Pago',
  'Etc/UTC': 'UTC',
};

export const normalizeTimezone = (tz?: string | null): string | null => {
  if (!tz || typeof tz !== 'string') return null;
  const trimmed = tz.trim();
  if (!trimmed) return null;
  const mapped = TIMEZONE_ALIASES[trimmed] ?? trimmed;
  return isValidTimezone(mapped) ? mapped : null;
};

/**
 * Validates if a timezone string is valid
 */
export const isValidTimezone = (tz: string): boolean => {
  if (!tz || typeof tz !== 'string') return false;
  
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (error) {
    return false;
  }
};
